import { z } from 'zod';
import {
  ClientTypeSchema,
  IdSchema,
  TimestampsSchema,
  paginated,
  softBoolean,
  softString,
  softStringArray,
} from './common.schema';
import { normalizePointer } from '@/lib/pointer-match';

// Backend entity: Client (admin-clients B2B). Read tolerante.

// Forma del fieldPriorityConfig según contrato real del backend:
//   {
//     sourcePriority: ["master", "sap", "axioma"],
//     fields: { "/media": ["master", "axioma"], "/npfCode": ["sap"], ... }
//   }
// Tolerante: ambos campos opcionales con default vacío.
export const FieldPriorityConfigSchema = z
  .object({
    sourcePriority: z
      .array(z.string())
      .nullable()
      .optional()
      .default([])
      .transform((v) => v ?? []),
    fields: z
      .record(z.string(), z.array(z.string()))
      .nullable()
      .optional()
      .default({})
      .transform((v) => v ?? {}),
  })
  .passthrough();
export type FieldPriorityConfig = z.infer<typeof FieldPriorityConfigSchema>;

export const ClientSchema = z
  .object({
    id: IdSchema,
    name: softString,
    slug: softString,
    apiKey: softString, // enmascarado/hash en list y get; nunca en claro.
    // En create/rotate el backend devuelve también el plaintext UNA VEZ
    // (rawApiKey/rawSecretKey). Después de eso, solo el hash. Mostrar al
    // admin para que copie y guarde.
    rawApiKey: z.string().nullable().optional(),
    rawSecretKey: z.string().nullable().optional(),
    type: ClientTypeSchema.optional().default('EXTERNAL'),
    allowedFields: softStringArray,
    // Pointers que el área lee pero el admin NO puede editar (release
    // 2026-08-21 reemplazó `hiddenFields` por `readOnlyFields`). Antes
    // ocultaba la key; ahora se muestra readonly con valor visible.
    // Reglas: NO admite `/sku` ni `/id`; debe estar DENTRO del scope
    // (allowedFields ∪ keys(fieldPriorityConfig.fields)) — inclusión, no
    // disjunción.
    readOnlyFields: softStringArray,
    canRead: softBoolean,
    canWrite: z
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .transform((v) => v ?? false),
    writableFields: softStringArray,
    webhooksEnabled: z
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .transform((v) => v ?? false),
    webhookUrl: z
      .string()
      .url()
      .nullable()
      .optional()
      .or(z.literal('').transform(() => null)),
    secretKey: z.string().nullable().optional(),
    // Flag derivado del back (release 2026-08-25-3):
    //   webhooksEnabled && webhookUrl != null && secretKey != null && isActive
    // El planner del backfill usa exactamente esta predicate; `secretKey` NO
    // viene en el payload (credencial), así que el flag es la única forma
    // que tiene el front de saber si un cliente es destino elegible.
    webhookReady: z
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .transform((v) => v ?? false),
    // Gates de notificación: si activos, solo se notifica al cliente cuando
    // el producto cumple la condición. Default false = sin gate (comportamiento previo).
    notifyOnlyIfActive: z
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .transform((v) => v ?? false),
    notifyOnlyIfWebActive: z
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .transform((v) => v ?? false),
    // Release back 2026-10-01: true suma un webhook por cada cambio de stock
    // del camino rápido de inventario (`PATCH /catalog/:slug/inventory/:sku`).
    // Solo sale si `allowedFields` lleva `/attributes/inventory/quantity`.
    notifyInventoryChanges: z
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .transform((v) => v ?? false),
    // Sources que el cliente tiene habilitadas para LEER datos.
    sourceSlugs: softStringArray,
    // Alcance por marca y categoría (release back 2026-09-11). Whitelist de
    // ids: lo listado se ve, el resto no.
    //
    // Vacío o ausente significa lo OPUESTO según el tipo de cliente: un
    // EXTERNAL no ve NADA (falla cerrado, igual que `allowedFields`) y un
    // INTERNAL ve TODO.
    //
    // `categoryIds` guarda solo lo OTORGADO. Una categoría otorgada arrastra a
    // sus descendientes, y el back no expone el conjunto expandido: se calcula
    // del árbol en la UI para que el admin vea qué está dando.
    brandIds: z.array(z.number().int()).nullable().optional().default([]),
    categoryIds: z.array(z.number().int()).nullable().optional().default([]),
    fieldPriorityConfig: FieldPriorityConfigSchema.nullable().optional(),
    // Habilita endpoint POST /products/query (projection schema). Costoso a
    // nivel DB; activar solo cuando el cliente lo necesita.
    canQuery: z
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .transform((v) => v ?? false),
    // Cap de queries B2B simultáneas por instancia. Cuando se alcanza, el
    // cliente recibe 429. Default backend: 20.
    maxConcurrentQueries: z
      .number()
      .int()
      .min(1)
      .max(500)
      .nullable()
      .optional()
      .default(20)
      .transform((v) => v ?? 20),
    // Si está activo (default), el backend corre `VehicleModelAliasResolver`
    // sobre `applicationModels` y descarta modelos sin alias curado.
    // Desactivar para clientes que necesitan los modelos raw del DB (ej:
    // clientes con su propio mapeo interno o que exigen fidelidad con la
    // fuente). Default backend: true.
    normalizeVehicleModels: z
      .boolean()
      .nullable()
      .optional()
      .default(true)
      .transform((v) => v ?? true),
    // Flags de capacidad que el gateway chequea antes de rutear. Deciden si el
    // cliente PUEDE llamar al endpoint; sin el flag responde 403.
    //   canRagSearch          -> GET /catalog/:clientSlug/rag/search
    //   canReadVehicleModels  -> modelos de vehículo del cliente: listado,
    //                            /vehicle-models/brands, detalle por id y
    //                            /vehicle-models/normalize.
    // Default backend: false en los dos.
    //
    // A diferencia del resto de los booleanos de acá, van SIN `.default()`: un
    // default los volvería requeridos en el tipo inferido y rompería toda
    // fixture que arma un `Client` literal. Quedan opcionales para que una
    // respuesta de backend viejo siga parseando; los consumidores resuelven el
    // ausente con `?? false`, que es el default del backend.
    canRagSearch: z.boolean().nullable().optional(),
    canReadVehicleModels: z.boolean().nullable().optional(),
    isActive: softBoolean,
    // Pointers en `fieldPriorityConfig.fields` que NO son canonicos (typo,
    // field removido del schema). Runtime del back los descarta al leer y
    // los reporta aca para que el admin corra `POST /admin/clients/:id/
    // cleanup-orphan-priorities`. Nunca falla el write; el banner del
    // FieldMatrixEditor los surface.
    orphanFieldPriorityKeys: softStringArray,
  })
  .merge(TimestampsSchema.partial());
export type Client = z.infer<typeof ClientSchema>;

export const ClientListSchema = paginated(ClientSchema);
export type ClientList = z.infer<typeof ClientListSchema>;

// Reglas del back sobre readOnlyFields (release 2026-08-21):
// - No puede contener /sku ni /id (sin identificador el payload no se correla).
// - Debe estar dentro del SCOPE del área — allowedFields ∪ keys del
//   fieldPriorityConfig.fields. La validación de scope va en el superRefine
//   porque necesita cruzar arrays.
const READ_ONLY_FIELDS_FORBIDDEN = new Set(['/sku', '/id']);

const ReadOnlyFieldsSchema = z
  .array(z.string().startsWith('/', 'Debe empezar con "/"'))
  .default([])
  .refine((arr) => arr.every((p) => !READ_ONLY_FIELDS_FORBIDDEN.has(p)), {
    message: 'No se puede marcar readonly /sku ni /id.',
  });

// Base object sin refinements — se usa para `.pick()` / `.partial()` que
// solo funcionan sobre ZodObject, no ZodEffects.
const ClientCreateObjectSchema = z.object({
  name: z.string().min(1).max(120),
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9-]+$/, 'Solo minúsculas, números y guiones'),
  type: ClientTypeSchema,
  canRead: z.boolean().default(true),
  canWrite: z.boolean().default(false),
  allowedFields: z.array(z.string()).default([]),
  readOnlyFields: ReadOnlyFieldsSchema,
  writableFields: z.array(z.string()).default([]),
  webhooksEnabled: z.boolean().default(false),
  webhookUrl: z.string().url().optional(),
  notifyOnlyIfActive: z.boolean().default(false),
  notifyOnlyIfWebActive: z.boolean().default(false),
  // Sin `.default()`: el back lo deja en false si no viene, y así un PATCH que
  // no lo toca no lo apaga.
  notifyInventoryChanges: z.boolean().optional(),
  sourceSlugs: z.array(z.string()).default([]),
  // Mandar el array REEMPLAZA todo el alcance: el back borra y recrea, igual
  // que con `sourceSlugs`. Omitir el campo lo deja intacto. Por eso el form
  // envía siempre el conjunto completo y nunca un delta.
  brandIds: z.array(z.number().int()).optional(),
  categoryIds: z.array(z.number().int()).optional(),
  fieldPriorityConfig: z
    .object({
      sourcePriority: z.array(z.string()).default([]),
      fields: z.record(z.string(), z.array(z.string())).default({}),
    })
    .optional(),
  canQuery: z.boolean().default(false),
  maxConcurrentQueries: z.number().int().min(1).max(500).default(20),
  normalizeVehicleModels: z.boolean().default(true),
  // Opcionales en escritura: el back los acepta en POST /admin/clients y en
  // PATCH /admin/clients/:id, y defaultea a false cuando no van. Sin
  // `.default()` para no inyectarlos en payloads que no los tocan.
  canRagSearch: z.boolean().optional(),
  canReadVehicleModels: z.boolean().optional(),
  isActive: z.boolean().default(true),
});

export const ClientCreateSchema = ClientCreateObjectSchema.superRefine((data, ctx) => {
  // readOnlyFields debe estar DENTRO del scope del área (inclusión, no
  // disjunción — release 2026-08-21). Scope = allowedFields ∪ keys(fieldPriorityConfig.fields).
  // Coverage por prefijo ASIMÉTRICO: el ancestro cubre a la hoja, la hoja
  // NO cubre al ancestro. Ej: allowedFields=['/attributes/pricing'] cubre
  // readOnlyFields=['/attributes/pricing/basePrice']; el reverso NO.
  //
  // NORMALIZAR ambos lados: legacy configs pueden estar en dot-form
  // (`attributes.pricing`) por SQL directo o data pre-normalización. El
  // back normaliza antes de comparar; nosotros hacemos lo mismo para no
  // rechazar configs válidos.
  //
  // Excepción: allowedFields vacío = área sin restricción de scope (todo
  // markable), skip esta validación.
  const allowed = (data.allowedFields ?? []).map(normalizePointer);
  const fieldsKeys = Object.keys(data.fieldPriorityConfig?.fields ?? {}).map(normalizePointer);
  const scope = [...allowed, ...fieldsKeys];
  if (allowed.length === 0) return;
  for (const raw of data.readOnlyFields ?? []) {
    const r = normalizePointer(raw);
    const covered = scope.some((s) => r === s || r.startsWith(`${s}/`));
    if (!covered) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['readOnlyFields'],
        message: `El área no lee "${raw}" — readOnlyFields debe estar dentro de allowedFields o de fieldPriorityConfig.fields.`,
      });
    }
  }
});
export type ClientCreate = z.infer<typeof ClientCreateSchema>;

// Reexportamos el objeto base para consumers que necesitan `.pick()` (form
// que divide el schema en tabs).
export { ClientCreateObjectSchema };

// El PATCH parcial pierde la validación de `superRefine` — ClientCreate la
// aporta arriba. Los callers de update deben re-validar la INCLUSIÓN contra
// el estado actual del cliente, no solo contra el diff: readOnlyFields tiene
// que caer dentro del scope del área (allowedFields ∪ keys de
// fieldPriorityConfig.fields), que es la misma lente que el back resuelve en
// `lensFields`. NO es `⊆ allowedFields`: en producción el área vtex marca
// organization, state y type como solo-lectura y ninguno está publicado —
// entran por prioridad de fuentes. En el form lo hacemos explícito
// (FieldMatrixEditor filtra contra allowedSet ∪ priorityFields).
export const ClientUpdateSchema = ClientCreateObjectSchema.partial();
export type ClientUpdate = z.infer<typeof ClientUpdateSchema>;
